import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as roomActions from 'features/room-builder/duck';
import * as userActions from 'core/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as staffActions from 'features/staff-and-rooms/duck';
import { createRoot } from 'react-dom/client';
import { withTranslation } from 'react-i18next';
import { TaskCardPreview, ExitTaskPrompt, TaskContent } from 'features/task-management';
import { getMappedTasks, getRoomAvailableTasks } from 'features/task-management/selectors';
import { isEmpty, sortBy } from 'lodash';
import { AlertNotice, MultiRadio, Spinner } from 'shared/components';
import { ROOM_STATUS } from 'shared/constants';
import { getOrderedRoomTypes } from 'shared/selectors';
import Completed from 'assets/svgs/check.svg';
import Expired from 'assets/svgs/expired.svg';
import { mapTimingOptions, applyActNatlTimingNames, getRoomTimingName } from 'shared/helpers';

import './ViewTasks.scss';

const filterTasks = (tasks, type, timing) => {
  if (type) {
    const filteredTasks = timing
      ? tasks.filter((task) => task.roomTiming === timing && task.roomType.id == type)
      : tasks.filter((task) => task.roomType.id == type);
    return sortBy(filteredTasks, ['displayOrder']);
  }
  return [];
};

const getTaskProps = (task, t) => {
  switch (task.status) {
    case 'completed':
      return {
        icon: <Completed />,
        label: t('common.labels.completed'),
      };
    case 'expired':
      return {
        icon: <Expired />,
        label: t('common.labels.expired'),
      };
    default:
      return {
        label: t('common.labels.upcoming'),
      };
  }
};

const getCurrentTask = (roomAvailableTasks, roomStatus) => {
  const lastCompletedIdx = roomAvailableTasks.findIndex(
    (t) => t.id === roomStatus.lastCompletedTaskId
  );
  return roomAvailableTasks[lastCompletedIdx + 1];
};

const setTypeOptions = (roomTypes, studyFlag) =>
  roomTypes.length
    ? roomTypes
        .map((option) => ({
          label: option.name,
          key: 'roomType-' + option.id,
          studyFlag: option.studyFlag,
          value: option.id,
          name: 'type',
        }))
        .filter((roomType) => roomType.studyFlag == studyFlag)
    : [];

class ViewTasks extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tasksFiltered: [],
      timingOptions: [],
      typeOptions: setTypeOptions(props.roomTypes, props.tcEvent.studyFlag),
      typeSelected: !isEmpty(props.room) && props.room.roomType ? props.room.roomType.id : '',
      timingSelected: !isEmpty(props.room) ? props.room.timing : '',
      currentTask: {},
      taskCardOpen: false,
    };

    this.renderTask = this.renderTask.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.hideSection = this.hideSection.bind(this);
    this.setDOM = this.setDOM.bind(this);
    this.onChangeType = this.onChangeType.bind(this);
    this.onChangeTiming = this.onChangeTiming.bind(this);
    this.scrollSmoothTo = this.scrollSmoothTo.bind(this);
    this.removeSticky = this.removeSticky.bind(this);
    this.removeLink = this.removeLink.bind(this);
    this.stickyRoot = null;
  }

  componentDidMount() {
    const { actions, match, tcEvent, roomTypes, tasks } = this.props;
    const { roomId } = this.props.match.params;
    actions.getRoomCompletedTasks(roomId);
    actions.getRoom(roomId).then(() => {
      if (!isEmpty(this.props.room)) {
        this.setState({
          timingSelected:
            this.props.room.timing ||
            mapTimingOptions(
              this.props.roomTypes,
              this.props.mappedRoomTiming,
              this.props.room.roomType ? this.props.room.roomType.id : this.props.room.id
            )[0].value,
        });
        if (!this.state.tasksFiltered) {
          this.setState({
            tasksFiltered: filterTasks(
              tasks,
              this.props.room.roomType ? this.props.room.roomType.id : this.props.room.id,
              this.props.room.timing || 'standard'
            ),
          });
        }
        if (!this.state.timingOptions) {
          this.setState({
            timingOptions: applyActNatlTimingNames(
              mapTimingOptions(
                this.props.roomTypes,
                this.props.mappedRoomTiming,
                this.props.room.roomType ? this.props.room.roomType.id : ''
              ),
              this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code
            ),
          });
        }
      }
    });
    actions.getRoomStatus(roomId);

    if (isEmpty(tcEvent)) {
      actions.getTcEvent(match.params.eventId).then(() => {
        if (!isEmpty(this.props.tcEvent)) {
          actions.getTasks(this.props.tcEvent.testingEvent.adminId.cycleId.id);
          this.props.actions.getTestCenterCycle(match.params.eventId);
          this.props.actions.getTcEventStaff(match.params.eventId);
          if (!roomTypes.length) {
            actions.getRoomTypes(match.params.eventId).then(() => {
              if (this.props.roomTypes.length) {
                this.setState({
                  typeOptions: setTypeOptions(this.props.roomTypes, this.props.tcEvent.studyFlag),
                  timingSelected:
                    this.props.room.timing ||
                    mapTimingOptions(
                      this.props.roomTypes,
                      this.props.mappedRoomTiming,
                      this.props.room.roomType ? this.props.room.roomType.id : this.props.room.id
                    )[0].value,
                  tasksFiltered: filterTasks(
                    tasks,
                    this.props.room.roomType
                      ? this.props.room.roomType.id
                      : this.props.roomTypes[0].id,
                    this.props.room.timing || 'standard'
                  ),
                });
              }
            });
          }
        }
      });
    } else {
      actions.getTasks(tcEvent.testingEvent.adminId.cycleId.id);
      this.props.actions.getTestCenterCycle(match.params.eventId);
      this.props.actions.getTcEventStaff(match.params.eventId);
      if (!roomTypes.length) {
        actions.getRoomTypes(match.params.eventId).then(() => {
          const { room } = this.props;
          if (this.props.roomTypes.length) {
            this.setState({
              typeOptions: setTypeOptions(this.props.roomTypes, this.props.tcEvent.studyFlag),
              timingSelected:
                room.timing ||
                mapTimingOptions(
                  this.props.roomTypes,
                  this.props.mappedRoomTiming,
                  room.roomType ? room.roomType.id : room.id
                )[0].value,
              tasksFiltered: filterTasks(
                tasks,
                room.roomType ? room.roomType.id : this.props.roomTypes[0].id,
                room.timing || 'standard'
              ),
            });
          }
        });
      }
    }

    // Setup scroll listener and DOM
    window.addEventListener('scroll', this.hideSection);
    this.setDOM();
  }

  componentWillUnmount() {
    this.removeSticky();
  }

  componentDidUpdate(prevProps) {
    const { mappedTasks, roomStatus, roomAvailableTasks } = this.props;
    const { tasksFiltered, typeSelected, timingSelected } = this.state;

    if (
      this.props.timerWarning !== prevProps.timerWarning &&
      this.props.timerWarning &&
      this.state.taskCardOpen
    ) {
      this.setState({
        taskCardOpen: false,
      });
    }

    if (
      !isEmpty(this.props.room) &&
      !this.props.room.loading &&
      this.props.room.status === ROOM_STATUS.inactive &&
      (isEmpty(prevProps.room) ||
        prevProps.room.loading ||
        prevProps.room.status !== ROOM_STATUS.inactive)
    ) {
      this.removeSticky();
    }
    if (
      (!isEmpty(this.props.room) &&
        !isEmpty(prevProps.room) &&
        !this.props.room.loading &&
        !prevProps.room.loading &&
        this.props.roomCompletedTasks.length > prevProps.roomCompletedTasks.length) ||
      (mappedTasks.length &&
        !tasksFiltered.length &&
        typeSelected &&
        timingSelected &&
        prevProps.mappedTasks !== mappedTasks)
    ) {
      if (isEmpty(getCurrentTask(roomAvailableTasks, roomStatus))) {
        this.removeLink();
      }
      this.setState({
        tasksFiltered: mappedTasks,
        currentTask: getCurrentTask(roomAvailableTasks, roomStatus) || {},
      });
    }

    if (this.props.tasks !== prevProps.tasks && this.props.room.roomType) {
      const { tasks, room } = this.props;
      if (!this.state.tasksFiltered.length) {
        this.setState({
          tasksFiltered: filterTasks(tasks, room.roomType.id, room.timing),
        });
      }
    }
    if (
      this.props.room.roomType &&
      this.props.room.roomType !== prevProps.roomType &&
      this.props.roomTypes.length &&
      !this.state.timingOptions.length
    ) {
      const { room } = this.props;
      this.setState({
        timingOptions: applyActNatlTimingNames(
          mapTimingOptions(
            this.props.roomTypes,
            this.props.mappedRoomTiming,
            room.roomType.id
          ),
          this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code
        ),
      });
    }
    if (
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      this.props.room !== prevProps.room &&
      !this.state.timingOptions.length &&
      !this.props.room.roomType
    ) {
      const timingCode = this.props.room.timing;
      const timingOptions = [
        {
          key: timingCode,
          label: getRoomTimingName(
            timingCode,
            this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code,
            this.props.mappedRoomTiming
          ),
          value: timingCode,
        },
      ];
      this.setState({
        timingOptions,
      });
    }
    if (
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      this.props.room !== prevProps.room
    ) {
      if (!this.props.roomTypes.length) {
        this.props.actions.getRoomTypes(this.props.match.params.eventId).then(() => {
          if (this.props.roomTypes.length) {
            const roomTypeId = this.props.room.roomType
              ? this.props.roomTypes.find((item) => item.id == this.props.room.roomType.id)
              : '';
            this.setState({
              typeSelected:
                this.props.room.roomType && roomTypeId?.id ? this.props.room.roomType.id : '',
            });
          }
        });
      }
    }
  }

  setDOM() {
    const section = document.getElementsByClassName('sticky-element')[0];
    const newDiv = document.createElement('div');

    newDiv.className = 'task-alert-sticky';
    section.appendChild(newDiv);

    const container = document.getElementsByClassName('task-alert-sticky')[0];
    if (!this.stickyRoot) {
      this.stickyRoot = createRoot(container);
    }
    this.stickyRoot.render(
      <div className="preview-sticky">
        <a href="#" id="navigateTaskLink" onClick={this.scrollSmoothTo}>
          {this.props.t('taskManagement.navigateToTask')}
        </a>
      </div>
    );
  }

  hideSection() {
    this.setState({
      hideAlert: window.pageYOffset > 65,
    });
  }

  toggleModal() {
    this.setState((prevState) => ({
      taskCardOpen: !prevState.taskCardOpen,
    }));
  }

  renderTask(task) {
    this.setState({
      renderedTask: {
        ...task,
      },
    });

    this.toggleModal();
  }

  onChangeType(e) {
    const { tasks } = this.props;
    const { value } = e.target;
    const timingOptions = applyActNatlTimingNames(
      mapTimingOptions(
        this.props.roomTypes,
        this.props.mappedRoomTiming,
        value
      ),
      this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code
    );
    const hasSameTiming = timingOptions.find((item) => item.value === this.state.timingSelected);

    const newTiming = !isEmpty(hasSameTiming)
      ? this.state.timingSelected
      : this.state.timingOptions[0].value;

    this.setState({
      typeSelected: value,
      tasksFiltered: filterTasks(tasks, value, newTiming),
      timingSelected: newTiming,
      timingOptions,
    });
  }

  onChangeTiming(e) {
    const { tasks } = this.props;
    const { typeSelected } = this.state;
    const { value } = e.target;

    this.setState({
      timingSelected: value,
      tasksFiltered: filterTasks(tasks, typeSelected, value),
    });
  }

  scrollSmoothTo(e) {
    e.preventDefault();
    let element = document.getElementById(this.state.currentTask.id);
    element.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }

  removeSticky() {
    const section = document.getElementsByClassName('sticky-element')[0];
    const child = document.getElementsByClassName('task-alert-sticky')[0];

    if (child) {
      if (this.stickyRoot) {
        this.stickyRoot.unmount();
        this.stickyRoot = null;
      }
      section.removeChild(child);
    }

    window.removeEventListener('scroll', this.hideSection);
  }

  removeLink() {
    const section = document.getElementsByClassName('preview-sticky')[0];
    const child = document.getElementById('navigateTaskLink');

    if (child) {
      section.removeChild(child);
    }
  }

  render() {
    const { room, roomTypes } = this.props;
    const { tasksFiltered, typeSelected, currentTask } = this.state;
    if (isEmpty(room) || !roomTypes.length) {
      return <Spinner />;
    }

    const inactiveRoom = room.status === ROOM_STATUS.inactive;
    return (
      <section className="preview-tasks-section">
        <div className="d-flex justify-content-between mb-3">
          <h4>{`${this.props.t('taskManagement.previewTasksTitle')} - ${room.name}`}</h4>
          <button className="btn btn-outline btn-small" onClick={this.props.history.goBack}>
            {this.props.t('common.labels.back')}
          </button>
        </div>
        <AlertNotice message={this.props.t('taskManagement.previewAlertMessage')} type="warning" />
        <div className="top-section">
          {inactiveRoom ? (
            <div className="inactive">
              <AlertNotice message={this.props.t('taskManagement.previewNotOpenWarningMessage')} />
              {!typeSelected && (
                <p className="fw-bold dark-gray">
                  {this.props.t('taskManagement.selectTypeMessage')}
                </p>
              )}
              <div className="radio-section">
                <MultiRadio
                  title={this.props.t('taskManagement.roomTypeLabel')}
                  radioList={this.state.typeOptions}
                  checkedValue={this.state.typeSelected}
                  onChange={this.onChangeType}
                />
                <MultiRadio
                  title={this.props.t('taskManagement.roomTimingLabel')}
                  radioList={this.state.timingOptions}
                  checkedValue={this.state.timingSelected}
                  onChange={this.onChangeTiming}
                />
              </div>
            </div>
          ) : (
            <div>
              {!this.state.hideAlert && !isEmpty(currentTask) && (
                <div>
                  <a href="#" onClick={this.scrollSmoothTo}>
                    {this.props.t('taskManagement.navigateToTask')}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        {tasksFiltered.map((task) => (
          <TaskCardPreview
            task={task}
            renderTask={this.renderTask}
            key={task.id}
            {...getTaskProps(task, this.props.t)}
          />
        ))}
        <ExitTaskPrompt />
        {this.state.renderedTask && (
          <TaskContent
            task={this.state.renderedTask}
            modalOpen={this.state.taskCardOpen}
            toggleModal={this.toggleModal}
            renderTask={this.renderTask}
            room={this.props.room}
            userId={this.props.user.id}
            submitTask={() => {}}
            removeCompleted={() => {}}
          />
        )}
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    tcEvent: state.tcEvent.currentEvent || {},
    tcCycle: state.tcEvent.testCenterCycle,
    room: state.roomOptions.room || {},
    roomStatus: state.taskManagement.roomStatus,
    tasks: state.taskManagement.tasks || [],
    roomCompletedTasks: state.taskManagement.roomCompletedTasks,
    user: state.users.currentUser || {},
    timerWarning: state.roomOptions.timerWarning,
    roomTypes: getOrderedRoomTypes(state),
    mappedTasks: getMappedTasks(state),
    roomAvailableTasks: getRoomAvailableTasks(state),
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...tcEventActions,
        ...taskActions,
        ...roomActions,
        ...userActions,
        ...staticActions,
        ...staffActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(ViewTasks))
);

ViewTasks.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    getRoom: PropTypes.func,
    getTasks: PropTypes.func,
    getRoomCompletedTasks: PropTypes.func,
    getTcEvent: PropTypes.func,
    getRoomStatus: PropTypes.func,
    getTestCenterCycle: PropTypes.func,
    getTcEventStaff: PropTypes.func,
    getRoomTypes: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      roomId: PropTypes.string,
      eventId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
  tasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  room: PropTypes.shape({
    name: PropTypes.string,
    status: PropTypes.string,
    timing: PropTypes.string,
    roomType: PropTypes.shape({
      id: PropTypes.number,
    }),
    loading: PropTypes.bool,
  }).isRequired,
  tcEvent: PropTypes.shape({
    studyFlag: PropTypes.bool,
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
  }),
  tcCycle: PropTypes.shape({}),
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  roomTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      studyFlag: PropTypes.bool,
    })
  ).isRequired,
  roomStatus: PropTypes.shape({
    lastCompletedTaskId: PropTypes.number,
  }).isRequired,
  roomCompletedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mappedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mappedRoomTiming: PropTypes.shape({}).isRequired,
  timerWarning: PropTypes.bool,
};
